import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { IconButton } from '@mui/material';
import type { GridColDef, GridRenderCellParams, GridRowId } from '@mui/x-data-grid';
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  gridDetailPanelExpandedRowsContentCacheSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import type { Currency } from '@vy/types';
import { DEFAULT_CURRENCY } from '@vy/types';
import type { KeyboardEvent, ReactNode } from 'react';
import { isValidElement, useMemo } from 'react';
import { Price } from '../../../../../components/design/Price';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';
import { formatNumberWithPrecision } from '../../../../../utils/number';
import type { UpcomingInvoicesReportItem } from '@vy/types';
import { TOTALS_ROW_ID } from '../../../reports-components/report-table-consts';
import {
  filterBillingForecastDateHeaders,
  getTotalBillingForecastDateHeaders,
} from './billing-forecast-report-calculations';
import type { BillingForecastSummaryRow } from './billing-forecast-report-aggregator';

export const BILLING_FORECAST_REPORT_KEYS = Object.freeze({
  CUSTOMER_NAME: 'customerName',
  TOTAL: 'total',
} satisfies Record<string, string>);

export const BILLING_FORECAST_DATE_COLUMN_CLASSNAME = 'vy-billing-forecast-date-column';

const EXPANDABLE_CELL_STYLE = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
} as const;

function ExpandableCell({ onToggle, children }: { onToggle: () => void; children: ReactNode }) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <div role="button" tabIndex={0} onClick={onToggle} onKeyDown={handleKeyDown} style={EXPANDABLE_CELL_STYLE}>
      {children}
    </div>
  );
}

function DetailPanelToggleCell({ id, value }: Pick<GridRenderCellParams, 'id' | 'value'>) {
  const apiRef = useGridApiContext();
  const contentCache = useGridSelector(apiRef, gridDetailPanelExpandedRowsContentCacheSelector);
  const hasDetail = isValidElement(contentCache[id]);
  const isExpanded = Boolean(value);

  if (!hasDetail) return null;

  return (
    <IconButton size="small" tabIndex={-1} aria-label={isExpanded ? 'Close' : 'Open'}>
      <KeyboardArrowDownIcon
        sx={{
          width: 16,
          height: 16,
          opacity: 1,
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 150ms ease',
        }}
      />
    </IconButton>
  );
}

function ExpandableCustomerCell({ id, customerName }: { id: GridRowId; customerName: string }) {
  const apiRef = useGridApiContext();
  const handleToggle = () => apiRef.current.toggleDetailPanel(id);

  return (
    <ExpandableCell onToggle={handleToggle}>
      <VyTableCell value={customerName} containerClassName="justify-start pl-4" />
    </ExpandableCell>
  );
}

function renderAmountCell(row: BillingForecastSummaryRow, value: number) {
  if (row.customer.id === TOTALS_ROW_ID) {
    if (value === 0) {
      return <VyTableCell value="--" containerClassName="justify-start" />;
    }
    return (
      <VyTableCell
        value={formatNumberWithPrecision(value, 2)}
        containerClassName="justify-start font-bold"
      />
    );
  }

  if (value === 0) {
    return <VyTableCell value="-" containerClassName="justify-start" />;
  }

  const currency = (row.currency ?? DEFAULT_CURRENCY) as Currency;

  return (
    <Price currency={currency} align="left" fractionDigits={2}>
      {value}
    </Price>
  );
}

export function useBillingForecastReportColumns(
  dateHeaders: string[],
): GridColDef<BillingForecastSummaryRow>[] {
  const toggleColumn = useMemo<GridColDef<BillingForecastSummaryRow>>(
    () => ({
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 40,
      minWidth: 40,
      renderCell: (params) => <DetailPanelToggleCell id={params.id} value={params.value} />,
    }),
    [],
  );

  const customerColumn = useMemo<GridColDef<BillingForecastSummaryRow>>(
    () => ({
      field: BILLING_FORECAST_REPORT_KEYS.CUSTOMER_NAME,
      headerName: 'Customer',
      valueGetter: (_value, row) => row.customer.name,
      flex: 1,
      minWidth: 238,
      renderCell: ({ row, id }) => {
        const customerName = row.customer.name;
        const isTotalsRow = row.customer.id === TOTALS_ROW_ID;

        if (isTotalsRow) {
          return (
            <VyTableCell
              value={<strong>{customerName}</strong>}
              containerClassName="justify-start pl-4"
            />
          );
        }

        return <ExpandableCustomerCell id={id} customerName={customerName} />;
      },
    }),
    [],
  );

  const dateColumns = useMemo(
    () =>
      dateHeaders.map<GridColDef<BillingForecastSummaryRow>>((dateHeader) => {
        const field = buildDateFieldId(dateHeader);

        return {
          field,
          headerName: dateHeader,
          flex: 1,
          minWidth: 160,
          type: 'number',
          headerAlign: 'left',
          align: 'left',
          valueGetter: (_value, row) => row.monthlyTotals.get(dateHeader) ?? 0,
          valueFormatter: value => formatNumberWithPrecision(Number(value ?? 0), 2),
          headerClassName: BILLING_FORECAST_DATE_COLUMN_CLASSNAME,
          cellClassName: BILLING_FORECAST_DATE_COLUMN_CLASSNAME,
          renderCell: ({ row }) => renderAmountCell(row, row.monthlyTotals.get(dateHeader) ?? 0),
        };
      }),
    [dateHeaders],
  );

  const totalColumn = useMemo<GridColDef<BillingForecastSummaryRow>>(
    () => ({
      field: BILLING_FORECAST_REPORT_KEYS.TOTAL,
      headerName: 'Total',
      flex: 1,
      minWidth: 160,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.total,
      valueFormatter: value => formatNumberWithPrecision(Number(value ?? 0), 2),
      renderCell: ({ row }) => renderAmountCell(row, row.total),
    }),
    [],
  );

  return useMemo(
    () => [toggleColumn, customerColumn, ...dateColumns, totalColumn],
    [toggleColumn, customerColumn, dateColumns, totalColumn],
  );
}

export function useBillingForecastDateHeaders(
  reportItems: UpcomingInvoicesReportItem[],
  fromDate?: Date | null,
  toDate?: Date | null,
): string[] {
  const allDateHeaders = useMemo(() => getTotalBillingForecastDateHeaders(reportItems), [reportItems]);

  return useMemo(
    () => filterBillingForecastDateHeaders(allDateHeaders, fromDate, toDate),
    [allDateHeaders, fromDate, toDate],
  );
}

function buildDateFieldId(dateHeader: string): string {
  return `date-${dateHeader.toLowerCase().replace(/\s+/g, '-')}`;
}
